import React, { useContext, useEffect, useState } from 'react'
import {
    Button,
    Card,
    CardBody,
    Col,
    CustomInput,
    Form,
    FormGroup,
    Input,
    Label,
    Nav,
    NavItem,
    NavLink,
    Row,
    Table
} from 'reactstrap'
import classnames from 'classnames'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'
import ApiService from '../../../data/services/ApiService'
import { GeneralContext } from '../../../context/'

const emptyDiscount = {
    type: 'store',
    name: '',
    description: '',
    discountType: 'percentage',
    value: '',
    categoryId: '',
    subcategoryId: '',
    materialId: '',
    isActive: true,
    appliesToInvitedArtists: false,
    startsAt: '',
    expiresAt: ''
}

const emptyCode = {
    code: '',
    name: '',
    description: '',
    discountType: 'percentage',
    value: '',
    hasProductDiscount: true,
    freeShipping: false,
    minimumCartValue: '',
    usageLimit: '',
    perUserLimit: 0,
    applicability: 'cart',
    categoryId: '',
    subcategoryId: '',
    productId: '',
    isActive: true,
    startsAt: '',
    expiresAt: ''
}

const dateValue = value => value ? String(value).substring(0, 10) : ''

const DiscountManager = () => {
    const { setLoading } = useContext(GeneralContext)
    const [activeTab, setActiveTab] = useState('discounts')
    const [discounts, setDiscounts] = useState([])
    const [codes, setCodes] = useState([])
    const [discount, setDiscount] = useState(emptyDiscount)
    const [code, setCode] = useState(emptyCode)
    const [categories, setCategories] = useState([])
    const [subcategories, setSubcategories] = useState([])
    const [materials, setMaterials] = useState([])

    useEffect(() => {
        fetchCatalogs()
        fetchData()
    }, []) // eslint-disable-line

    const fetchCatalogs = async () => {
        try {
            const [categoriesResponse, subcategoriesResponse, materialsResponse] = await Promise.all([
                ApiService.getCategories(),
                ApiService.getSubcategories(),
                ApiService.getActiveMaterials()
            ])

            setCategories(categoriesResponse.data.data || [])
            setSubcategories(subcategoriesResponse.data.data || [])
            setMaterials(materialsResponse.data.materials || [])
        } catch (error) {
            toastr.error('No pude cargar catálogos para descuentos', 'Error')
        }
    }

    const fetchData = async () => {
        try {
            setLoading(true)
            const [discountsResponse, codesResponse] = await Promise.all([
                ApiService.getDiscounts(),
                ApiService.getDiscountCodes()
            ])

            setDiscounts(discountsResponse.data.discounts || [])
            setCodes(codesResponse.data.codes || [])
        } catch (error) {
            toastr.error('No pude cargar descuentos y cupones', 'Error')
        } finally {
            setLoading(false)
        }
    }

    const selectOptions = items => items.map(item => (
        <option key={item.id} value={item.id}>{item.name}</option>
    ))

    const relationName = item => {
        if (item.type === 'personalized' || item.applicability === 'personalized') return 'Personalizados'
        if (item.type === 'artist' || item.applicability === 'artist') return 'Artistas Invitados'
        return item.category?.name || item.subcategory?.name || item.material?.name || item.product?.name || 'Toda la tienda'
    }
    const couponBenefits = item => {
        const benefits = []
        if (item.discountType === 'percentage') benefits.push(`${item.value}% en productos`)
        if (item.discountType === 'fixed') benefits.push(`$${item.value} en productos`)
        if (item.freeShipping) benefits.push('Envío gratis')
        return benefits.join(' + ')
    }

    const submitDiscount = async (event) => {
        event.preventDefault()
        if (!discount.name || !discount.value) {
            toastr.error('Nombre y valor son requeridos', 'Error')
            return
        }

        try {
            setLoading(true)
            if (discount.id) {
                await ApiService.updateDiscount(discount)
                toastr.success('Descuento actualizado', 'Éxito')
            } else {
                await ApiService.createDiscount(discount)
                toastr.success('Descuento agregado', 'Éxito')
            }
            setDiscount(emptyDiscount)
            fetchData()
        } catch (error) {
            const message = error?.response?.data?.message || 'No pude guardar el descuento'
            toastr.error(message, 'Error')
        } finally {
            setLoading(false)
        }
    }

    const submitCode = async (event) => {
        event.preventDefault()
        if (!code.code || !code.name) {
            toastr.error('Código y nombre son requeridos', 'Error')
            return
        }
        if (!code.hasProductDiscount && !code.freeShipping) {
            toastr.error('Selecciona un descuento en productos, envío gratis o ambos', 'Error')
            return
        }
        if (code.hasProductDiscount && (!code.value || Number(code.value) <= 0 || (code.discountType === 'percentage' && Number(code.value) > 100))) {
            toastr.error('Ingresa un valor válido para el descuento en productos', 'Error')
            return
        }

        try {
            setLoading(true)
            if (code.id) {
                await ApiService.updateDiscountCode(code)
                toastr.success('Cupón actualizado', 'Éxito')
            } else {
                await ApiService.createDiscountCode(code)
                toastr.success('Cupón agregado', 'Éxito')
            }
            setCode(emptyCode)
            fetchData()
        } catch (error) {
            const message = error?.response?.data?.message || 'No pude guardar el cupón'
            toastr.error(message, 'Error')
        } finally {
            setLoading(false)
        }
    }

    const deleteDiscount = async (id) => {
        await ApiService.deleteDiscount(id)
        toastr.success('Descuento eliminado', 'Éxito')
        fetchData()
    }

    const deleteCode = async (id) => {
        await ApiService.deleteDiscountCode(id)
        toastr.success('Cupón eliminado', 'Éxito')
        fetchData()
    }

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Nav tabs>
                        <NavItem>
                            <NavLink className={classnames({ active: activeTab === 'discounts' })} onClick={() => setActiveTab('discounts')}>
                                Descuentos automáticos
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink className={classnames({ active: activeTab === 'codes' })} onClick={() => setActiveTab('codes')}>
                                Cupones
                            </NavLink>
                        </NavItem>
                    </Nav>
                </CardBody>
            </Card>

            {activeTab === 'discounts' && (
                <Row>
                    <Col md={4} xs={12}>
                        <Card>
                            <CardBody>
                                <Form onSubmit={submitDiscount}>
                                    <FormGroup>
                                        <Label>Nombre *</Label>
                                        <Input value={discount.name} onChange={e => setDiscount({ ...discount, name: e.target.value })} />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Descripción</Label>
                                        <Input value={discount.description || ''} onChange={e => setDiscount({ ...discount, description: e.target.value })} />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Aplica a</Label>
                                        <Input type="select" value={discount.type} onChange={e => setDiscount({ ...discount, type: e.target.value, categoryId: '', subcategoryId: '', materialId: '', appliesToInvitedArtists: e.target.value === 'artist' ? true : discount.appliesToInvitedArtists })}>
                                            <option value="store">Toda la tienda</option>
                                            <option value="category">Categoría</option>
                                            <option value="subcategory">Subcategoría</option>
                                            <option value="material">Material</option>
                                            <option value="personalized">Personalizados</option>
                                            <option value="artist">Artistas Invitados</option>
                                        </Input>
                                    </FormGroup>
                                    {discount.type === 'category' && <FormGroup><Label>Categoría</Label><Input type="select" value={discount.categoryId || ''} onChange={e => setDiscount({ ...discount, categoryId: e.target.value })}><option value="">Seleccionar</option>{selectOptions(categories)}</Input></FormGroup>}
                                    {discount.type === 'subcategory' && <FormGroup><Label>Subcategoría</Label><Input type="select" value={discount.subcategoryId || ''} onChange={e => setDiscount({ ...discount, subcategoryId: e.target.value })}><option value="">Seleccionar</option>{selectOptions(subcategories)}</Input></FormGroup>}
                                    {discount.type === 'material' && <FormGroup><Label>Material</Label><Input type="select" value={discount.materialId || ''} onChange={e => setDiscount({ ...discount, materialId: e.target.value })}><option value="">Seleccionar</option>{selectOptions(materials)}</Input></FormGroup>}
                                    <Row>
                                        <Col sm={6}><FormGroup><Label>Tipo</Label><Input type="select" value={discount.discountType} onChange={e => setDiscount({ ...discount, discountType: e.target.value })}><option value="percentage">Porcentaje</option><option value="fixed">Monto fijo</option></Input></FormGroup></Col>
                                        <Col sm={6}><FormGroup><Label>Valor *</Label><Input type="number" value={discount.value} onChange={e => setDiscount({ ...discount, value: e.target.value })} /></FormGroup></Col>
                                    </Row>
                                    <Row>
                                        <Col sm={6}><FormGroup><Label>Inicio</Label><Input type="date" value={dateValue(discount.startsAt)} onChange={e => setDiscount({ ...discount, startsAt: e.target.value })} /></FormGroup></Col>
                                        <Col sm={6}><FormGroup><Label>Expira</Label><Input type="date" value={dateValue(discount.expiresAt)} onChange={e => setDiscount({ ...discount, expiresAt: e.target.value })} /></FormGroup></Col>
                                    </Row>
                                    {discount.type !== 'artist' && (
                                        <React.Fragment>
                                            <CustomInput
                                                type="switch"
                                                id="discount-applies-to-invited-artists"
                                                label="Compatibilidad: incluir artistas invitados en este descuento"
                                                checked={Boolean(discount.appliesToInvitedArtists)}
                                                onChange={() => setDiscount({ ...discount, appliesToInvitedArtists: !discount.appliesToInvitedArtists })}
                                            />
                                            <small className="text-muted d-block mt-1">
                                                Para campañas nuevas usa “Artistas Invitados” en Aplica a. Este switch conserva descuentos generales existentes que también absorbe Canvas Express.
                                            </small>
                                        </React.Fragment>
                                    )}
                                    <CustomInput type="switch" id="discount-active" label="Activo" checked={discount.isActive} onChange={() => setDiscount({ ...discount, isActive: !discount.isActive })} />
                                    <Button color="primary" className="mt-3" type="submit"><i className="mdi mdi-check"></i> Guardar</Button>
                                    {discount.id && <Button className="mt-3 ml-2" onClick={() => setDiscount(emptyDiscount)}>Cancelar</Button>}
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={8} xs={12}>
                        <Card>
                            <div className="table-responsive">
                                <Table striped>
                                    <thead><tr><th>Nombre</th><th>Aplica a</th><th>Descuento</th><th>Artistas invitados</th><th>Activo</th><th>...</th></tr></thead>
                                    <tbody>
                                        {discounts.map(item => (
                                            <tr key={item.id}>
                                                <td>{item.name}</td><td>{relationName(item)}</td><td>{item.discountType === 'percentage' ? `${item.value}%` : `$${item.value}`}</td><td>{item.appliesToInvitedArtists ? 'Sí' : 'No'}</td><td>{item.isActive ? 'Sí' : 'No'}</td>
                                                <td><Button size="sm" onClick={() => setDiscount({ ...item, startsAt: dateValue(item.startsAt), expiresAt: dateValue(item.expiresAt) })}><i className="mdi mdi-pencil"></i></Button> <Button size="sm" onClick={() => deleteDiscount(item.id)}><i className="mdi mdi-trash-can"></i></Button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'codes' && (
                <Row>
                    <Col md={4} xs={12}>
                        <Card><CardBody>
                            <Form onSubmit={submitCode}>
                                <Row>
                                    <Col sm={6}><FormGroup><Label>Código *</Label><Input value={code.code} onChange={e => setCode({ ...code, code: e.target.value.toUpperCase() })} /></FormGroup></Col>
                                    <Col sm={6}><FormGroup><Label>Nombre *</Label><Input value={code.name} onChange={e => setCode({ ...code, name: e.target.value })} /></FormGroup></Col>
                                </Row>
                                <FormGroup><Label>Descripción</Label><Input value={code.description || ''} onChange={e => setCode({ ...code, description: e.target.value })} /></FormGroup>
                                <FormGroup><Label>Aplica a</Label><Input type="select" value={code.applicability} onChange={e => setCode({ ...code, applicability: e.target.value, categoryId: '', subcategoryId: '', productId: '' })}><option value="cart">Carrito</option><option value="category">Categoría</option><option value="subcategory">Subcategoría</option><option value="product">Producto</option><option value="personalized">Personalizados</option><option value="artist">Artistas Invitados</option></Input></FormGroup>
                                {code.applicability === 'category' && <FormGroup><Label>Categoría</Label><Input type="select" value={code.categoryId || ''} onChange={e => setCode({ ...code, categoryId: e.target.value })}><option value="">Seleccionar</option>{selectOptions(categories)}</Input></FormGroup>}
                                {code.applicability === 'subcategory' && <FormGroup><Label>Subcategoría</Label><Input type="select" value={code.subcategoryId || ''} onChange={e => setCode({ ...code, subcategoryId: e.target.value })}><option value="">Seleccionar</option>{selectOptions(subcategories)}</Input></FormGroup>}
                                {code.applicability === 'product' && <FormGroup><Label>Product ID</Label><Input value={code.productId || ''} onChange={e => setCode({ ...code, productId: e.target.value })} /></FormGroup>}
                                <CustomInput type="switch" id="code-product-discount" label="Descuento en productos" checked={code.hasProductDiscount !== false} onChange={() => setCode({ ...code, hasProductDiscount: code.hasProductDiscount === false, discountType: code.hasProductDiscount === false ? 'percentage' : null, value: code.hasProductDiscount === false ? '' : null })} />
                                {code.hasProductDiscount !== false && <Row className="mt-2">
                                    <Col sm={6}><FormGroup><Label>Tipo</Label><Input type="select" value={code.discountType || 'percentage'} onChange={e => setCode({ ...code, discountType: e.target.value })}><option value="percentage">Porcentaje</option><option value="fixed">Monto fijo</option></Input></FormGroup></Col>
                                    <Col sm={6}><FormGroup><Label>Valor *</Label><Input type="number" min="0" max={code.discountType === 'percentage' ? 100 : undefined} value={code.value || ''} onChange={e => setCode({ ...code, value: e.target.value })} /></FormGroup></Col>
                                </Row>}
                                <CustomInput type="switch" id="code-free-shipping" label="Envío gratis" checked={Boolean(code.freeShipping)} onChange={() => setCode({ ...code, freeShipping: !code.freeShipping })} />
                                <Row className="mt-2">
                                    <Col sm={6}><FormGroup><Label>Mínimo carrito</Label><Input type="number" value={code.minimumCartValue || ''} onChange={e => setCode({ ...code, minimumCartValue: e.target.value })} /></FormGroup></Col>
                                    <Col sm={6}><FormGroup><Label>Límite de usos</Label><Input type="number" value={code.usageLimit || ''} onChange={e => setCode({ ...code, usageLimit: e.target.value })} /></FormGroup></Col>
                                    <Col sm={6}><FormGroup><Label>Límite por usuario</Label><Input type="number" value={code.perUserLimit || 0} onChange={e => setCode({ ...code, perUserLimit: e.target.value })} /></FormGroup></Col>
                                    <Col sm={6}><FormGroup><Label>Inicio</Label><Input type="date" value={dateValue(code.startsAt)} onChange={e => setCode({ ...code, startsAt: e.target.value })} /></FormGroup></Col>
                                    <Col sm={6}><FormGroup><Label>Expira</Label><Input type="date" value={dateValue(code.expiresAt)} onChange={e => setCode({ ...code, expiresAt: e.target.value })} /></FormGroup></Col>
                                </Row>
                                <CustomInput type="switch" id="code-active" label="Activo" checked={code.isActive} onChange={() => setCode({ ...code, isActive: !code.isActive })} />
                                <Button color="primary" className="mt-3" type="submit"><i className="mdi mdi-check"></i> Guardar</Button>
                                {code.id && <Button className="mt-3 ml-2" onClick={() => setCode(emptyCode)}>Cancelar</Button>}
                            </Form>
                        </CardBody></Card>
                    </Col>
                    <Col md={8} xs={12}>
                        <Card><div className="table-responsive"><Table striped>
                            <thead><tr><th>Código</th><th>Nombre</th><th>Aplica a</th><th>Descuento</th><th>Usos</th><th>Activo</th><th>...</th></tr></thead>
                            <tbody>{codes.map(item => (
                                <tr key={item.id}>
                                    <td>{item.code}</td><td>{item.name}</td><td>{relationName(item)}</td><td>{couponBenefits(item)}</td><td>{item.usedCount}/{item.usageLimit || '∞'}</td><td>{item.isActive ? 'Sí' : 'No'}</td>
                                    <td><Button size="sm" onClick={() => setCode({ ...item, hasProductDiscount: Boolean(item.discountType), startsAt: dateValue(item.startsAt), expiresAt: dateValue(item.expiresAt) })}><i className="mdi mdi-pencil"></i></Button> <Button size="sm" onClick={() => deleteCode(item.id)}><i className="mdi mdi-trash-can"></i></Button></td>
                                </tr>
                            ))}</tbody>
                        </Table></div></Card>
                    </Col>
                </Row>
            )}
        </React.Fragment>
    )
}

export default DiscountManager
